import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faBuilding,
  faCheck,
  faSearch
} from "@fortawesome/free-solid-svg-icons";
import { FixedSizeList as List } from "react-window";

const ChangeCompanyModal = ({ isOpen, onClose, companies, selectedCompany, onSelect, t }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [localSelected, setLocalSelected] = useState(selectedCompany);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setLocalSelected(selectedCompany);
      setSearchTerm("");
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen, selectedCompany]);

  const filteredCompanies = useMemo(() => {
    if (!companies) return [];
    if (!searchTerm) return companies;

    const search = searchTerm.toLowerCase();
    return companies.filter(company =>
      company.name?.toLowerCase().includes(search) ||
      company.id?.toString().includes(search) ||
      company.nipt?.toLowerCase().includes(search)
    );
  }, [companies, searchTerm]);

  const handleSave = () => {
    if (localSelected) {
      onSelect(localSelected);
      onClose();
    }
  };

  if (!isOpen) return null;

  const ITEM_HEIGHT = 72;
  const LIST_HEIGHT = Math.min(filteredCompanies.length * ITEM_HEIGHT, 350);

  const CompanyRow = ({ index, style }) => {
    const company = filteredCompanies[index];
    const isSelected = localSelected === company.id?.toString();
    const isDisabled = company.authProviderAllowed === false;

    return (
      <div style={{ ...style, paddingRight: '8px' }}>
        <div
          className={`custom-modal__company-item ${isSelected ? 'custom-modal__company-item--selected' : ''} ${isDisabled ? 'custom-modal__company-item--disabled' : ''}`}
          onClick={() => {
            if (!isDisabled) {
              setLocalSelected(company.id?.toString());
            }
          }}
        >
          <div className="custom-modal__company-icon">
            <FontAwesomeIcon icon={faBuilding} />
          </div>
          <div className="custom-modal__company-info">
            <div className="custom-modal__company-name">{company.name}</div>
            <div className="custom-modal__company-id">
              {company.nipt || `ID: ${company.id}`}
            </div>
          </div>
          <div className="custom-modal__company-check">
            <FontAwesomeIcon icon={faCheck} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="custom-modal__overlay custom-modal__overlay--visible"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="custom-modal__content">
        <div className="custom-modal__header">
          <h2>{t("navbar.changeCompany")}</h2>
          <button className="custom-modal__header-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="custom-modal__body">
          <div className="custom-modal__search">
            <span className="custom-modal__search-icon">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <input
              ref={searchInputRef}
              type="text"
              className="custom-modal__search-input"
              placeholder={t("common.search") + "..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {companies?.length > 0 && (
              <span className="custom-modal__search-count">
                {filteredCompanies.length} / {companies.length}
              </span>
            )}
          </div>

          {filteredCompanies.length > 0 ? (
            <List
              height={LIST_HEIGHT}
              itemCount={filteredCompanies.length}
              itemSize={ITEM_HEIGHT}
              width="100%"
            >
              {CompanyRow}
            </List>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              {t("common.noResults")}
            </div>
          )}
        </div>
        <div className="custom-modal__footer">
          <button className="custom-modal__btn custom-modal__btn--secondary" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button
            className="custom-modal__btn custom-modal__btn--primary"
            onClick={handleSave}
            disabled={!localSelected}
          >
            <FontAwesomeIcon icon={faCheck} />
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeCompanyModal;
